// Models
import { CallIssueType } from 'src/app/models/call-issue/call-issue';
import { PeerStats } from 'src/app/models/peer-stats/peer-stats';

// Services
import { IssueDetectionService } from 'src/app/services/issue-detection/issue-detection.service';
import { IssueRemediationService } from 'src/app/services/issue-remediation/issue-remediation.service';
import { ParticipantsListService } from '../participants-list/participants-list.service';

export const ST_TRESHOLD = 50; // %
export const DT_THESHOLD = 20; // %
export const BITRATE_OUT_THRESHOLD = 25; // %
export const BITRATE_IN_THRESHOLD = 25; // %
export const JITTER_THRESHOLD = 600; // nb
export const RTT_THRESHOLD = Infinity; // nb
export const AV_DELAY_THRESHOLD = 300; // nb
export const DECODE_TIME_THRESHOLD = 75; // %
export const ENCODE_TIME_THRESHOLD = 75; // %
export const FPS_THRESHOLD = 10; // nb

export class StatsIssueChecker {
  constructor(
    private issueDetection: IssueDetectionService,
    private issueRemediation: IssueRemediationService,
    private participants: ParticipantsListService,
  ) {}

  checkIssues(participant: string, report: PeerStats, userId: string, shareAudioTrackId: string): void {
    if (report.stVolatility > ST_TRESHOLD) {
      this.issueDetection.logEvent(CallIssueType.st_volatility, null, report.stVolatility);
      if (this.participants.participants[userId].isSharing) {
        this.issueDetection.logEvent(CallIssueType.st_volatility, null, report.stVolatility, true);
      }
    }
    if (report.dtVolatility > DT_THESHOLD) {
      this.issueDetection.logEvent(CallIssueType.dt_volatility, null, report.dtVolatility);
      if (this.participants.participants[userId].isSharing) {
        this.issueDetection.logEvent(CallIssueType.dt_volatility, null, report.dtVolatility, true);
      }
    }

    this.checkParticipantIssues(participant, report, userId);
    this.checkShareIssues(participant, report, userId, shareAudioTrackId);
  }

  checkParticipantIssues(participant: string, report: PeerStats, userId: string): void {
    if (report.participantsStats[participant]) {
      const participantReport = report.participantsStats[participant];
      const other = this.participants.participants[participant];
      const self = this.participants.participants[userId];

      if (other.videoSwitch) {
        const remoteFramerate = this.issueRemediation.participantRemediations.remote[participant].video.framerate;
        const remoteBitrate = this.issueRemediation.participantRemediations.remote[participant].video.bitrate;
        if (participantReport.video.decodeTimePerFrame > (1000 / remoteFramerate) * (DECODE_TIME_THRESHOLD / 100)) {
          this.issueDetection.logEvent(CallIssueType.decode_time, participant, participantReport.video.decodeTimePerFrame);
        }
        if (participantReport.incomingBitrate > 0 && participantReport.incomingBitrate < remoteBitrate * (BITRATE_IN_THRESHOLD / 100)) {
          this.issueDetection.logEvent(CallIssueType.bandwidth_in, participant, participantReport.incomingBitrate);
        }
        if ((participantReport.video.jitterIn * 1000) > JITTER_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.video_jitter_in, participant, participantReport.video.jitterIn * 1000);
        }
        if (participantReport.video.fpsIn > 0 && participantReport.video.fpsIn < FPS_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.fps_in, participant, participantReport.video.fpsIn);
        }
      }
      if (other.audioSwitch) {
        if ((participantReport.audio.jitterIn * 1000) > JITTER_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.audio_jitter_in, participant, participantReport.audio.jitterIn * 1000);
        }
      }
      if (other.videoSwitch && other.audioSwitch) {
        if (participantReport.avDelay > AV_DELAY_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.av_delay, participant, participantReport.avDelay);
        }
      }

      if (self.videoSwitch) {
        const localFramerate = this.issueRemediation.participantRemediations.local[participant].video.framerate;
        const localBitrate = this.issueRemediation.participantRemediations.local[participant].video.bitrate;
        if (participantReport.video.encodeTimePerFrame > (1000 / localFramerate) * (ENCODE_TIME_THRESHOLD / 100)) {
          this.issueDetection.logEvent(CallIssueType.encode_time, participant, participantReport.video.encodeTimePerFrame);
        }
        if (participantReport.outgoingBitrate > 0 && participantReport.outgoingBitrate < localBitrate * (BITRATE_OUT_THRESHOLD / 100)) {
          this.issueDetection.logEvent(CallIssueType.bandwidth_out, participant, participantReport.outgoingBitrate);
        }
        if ((participantReport.video.jitterOut * 1000) > JITTER_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.video_jitter_out, participant, participantReport.video.jitterOut * 1000);
        }
        if (participantReport.video.fpsOut > 0 && participantReport.video.fpsOut < FPS_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.fps_out, participant, participantReport.video.fpsOut);
        }
      }
      if (self.audioSwitch) {
        if ((participantReport.audio.jitterOut * 1000) > JITTER_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.audio_jitter_out, participant, participantReport.audio.jitterOut * 1000);
        }
      }
      if (self.videoSwitch || self.audioSwitch) {
        if (participantReport.rtt > RTT_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.rtt, participant, participantReport.rtt);
        }
      }
    }
  }

  checkShareIssues(participant: string, report: PeerStats, userId: string, shareAudioTrackId: string): void {
    if (report.shareStats[participant]) {
      const shareReport = report.shareStats[participant];
      const other = this.participants.participants[participant];
      const self = this.participants.participants[userId];

      if (other.isSharing) {
        const remoteFramerate = this.issueRemediation.shareRemediations.remote[participant].video.framerate;
        if (shareReport.video.decodeTimePerFrame > (1000 / remoteFramerate) * (1 - DECODE_TIME_THRESHOLD / 100)) {
          this.issueDetection.logEvent(CallIssueType.decode_time, participant, shareReport.video.decodeTimePerFrame, true);
        }
        if ((shareReport.video.jitterIn * 1000) > JITTER_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.video_jitter_in, participant, shareReport.video.jitterIn * 1000, true);
        }
        if (shareAudioTrackId) {
          if ((shareReport.audio.jitterIn * 1000) > JITTER_THRESHOLD) {
            this.issueDetection.logEvent(CallIssueType.audio_jitter_in, participant, shareReport.audio.jitterIn * 1000, true);
          }
          if (shareReport.avDelay > AV_DELAY_THRESHOLD) {
            this.issueDetection.logEvent(CallIssueType.av_delay, participant, shareReport.avDelay, true);
          }
        }
      }

      if (self.isSharing) {
        const localFramerate = this.issueRemediation.shareRemediations.local[participant].video.framerate;
        if (shareReport.video.encodeTimePerFrame > (1000 / localFramerate) * (1 - ENCODE_TIME_THRESHOLD / 100)) {
          this.issueDetection.logEvent(CallIssueType.encode_time, participant, shareReport.video.encodeTimePerFrame, true);
        }
        if ((shareReport.video.jitterOut * 1000) > JITTER_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.video_jitter_out, participant, shareReport.video.jitterOut * 1000, true);
        }
        if (shareReport.rtt > RTT_THRESHOLD) {
          this.issueDetection.logEvent(CallIssueType.rtt, participant, shareReport.rtt, true);
        }
        if (shareAudioTrackId) {
          if ((shareReport.audio.jitterOut * 1000) > JITTER_THRESHOLD) {
            this.issueDetection.logEvent(CallIssueType.audio_jitter_out, participant, shareReport.audio.jitterOut * 1000, true);
          }
        }
      }
    }
  }
}
